import { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import {
  criteriaForDevices,
  isIntroCriterion,
  selectedDeviceNames,
} from '../../compass/compassModel';
import CompassForm from './CompassForm';

function CriterionLine({ field, value, onChange, elementId, highlight = false }) {
  return (
    <div className="compass-criterion" id={elementId}>
      <p>
        <span className="compass-id">{field.criterion_type} {field.criterion_id || '—'}</span>
        {field.field_label}
      </p>
      <div className={`compass-options${highlight ? ' is-next' : ''}`} role="radiogroup" aria-label={field.field_label}>
        {['Met', 'Not met'].map((option) => (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={value === option}
            className={value === option ? 'is-selected' : ''}
            onClick={() => onChange(field.field_key, option)}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

function Bundle({ bundle, values, onChange, idPrefix, highlightFieldKey = '' }) {
  const [first, ...rest] = bundle.rows;
  const intro = bundle.rows.length > 1 && isIntroCriterion(first.field_label);
  const lines = intro ? rest : bundle.rows;
  return (
    <div>
      {intro ? <p className="compass-hint" style={{ marginBottom: '0.35rem' }}>{first.field_label}</p> : null}
      {lines.map((field) => (
        <CriterionLine
          key={field.field_key}
          field={field}
          value={values[field.field_key] || ''}
          onChange={onChange}
          elementId={`${idPrefix}-criterion-${field.field_key}`}
          highlight={highlightFieldKey === field.field_key}
        />
      ))}
    </div>
  );
}

export default function CriteriaPanel({
  criteria,
  deviceFields,
  deviceValues,
  values,
  onChange,
  reviewerFields,
  onEditDevices,
  idPrefix,
  formTitle = '',
  highlightFieldKey = '',
}) {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(() => new Set(['All']));
  const selected = selectedDeviceNames(deviceFields, deviceValues);
  const groups = useMemo(
    () => criteriaForDevices(criteria, selected),
    [criteria, selected],
  );

  useEffect(() => {
    if (!highlightFieldKey) return;
    const group = groups.find((item) => item.rows.some((bundle) => (
      bundle.rows.some((field) => field.field_key === highlightFieldKey)
    )));
    if (!group) return;
    setExpanded((current) => {
      if (current.has(group.device)) return current;
      const next = new Set(current);
      next.add(group.device);
      return next;
    });
  }, [highlightFieldKey, groups]);
  const needle = query.trim().toLowerCase();
  const visibleGroups = groups
    .map((group) => ({
      ...group,
      rows: needle
        ? group.rows
          .map((bundle) => ({
            ...bundle,
            rows: bundle.rows.filter((field) => field.field_label.toLowerCase().includes(needle)),
          }))
          .filter((bundle) => bundle.rows.length)
        : group.rows,
    }))
    .filter((group) => group.rows.length);

  return (
    <div>
      <p className="compass-note">
        Every subject is checked against the criteria that apply to all devices.
        Device-specific criteria appear after you select the implants on Eligibility and devices.
      </p>
      <button type="button" className="compass-text-button" onClick={onEditDevices} style={{ margin: '0.6rem 0 0.8rem' }}>
        Edit selected devices{selected.length ? ` (${selected.length})` : ''}
      </button>
      <Form.Control
        className="compass-search"
        type="search"
        placeholder="Search criteria"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        aria-label="Search criteria"
      />
      {!selected.length && (
        <p className="compass-hint">No implants selected yet. The list below is the criteria that apply to every subject.</p>
      )}
      {visibleGroups.map((group) => {
        const count = group.rows.reduce((sum, bundle) => sum + bundle.rows.length, 0);
        const title = group.device === 'All' ? 'All subjects' : group.device;
        const isOpen = Boolean(needle) || expanded.has(group.device);
        return (
          <div key={group.device} className="compass-device">
            <button
              type="button"
              className="compass-nav-button"
              aria-expanded={isOpen}
              onClick={() => {
                setExpanded((current) => {
                  const next = new Set(current);
                  if (next.has(group.device)) next.delete(group.device);
                  else next.add(group.device);
                  return next;
                });
              }}
            >
              <span>{title}</span>
              <small>{count}</small>
            </button>
            {isOpen ? group.rows.map((bundle) => (
              <Bundle
                key={bundle.key}
                bundle={bundle}
                values={values}
                onChange={onChange}
                idPrefix={idPrefix}
                highlightFieldKey={highlightFieldKey}
              />
            )) : null}
          </div>
        );
      })}
      {needle && !visibleGroups.length ? <p>No criteria match that search.</p> : null}
      {reviewerFields.length > 0 && (
        <CompassForm
          fields={reviewerFields}
          values={values}
          onChange={onChange}
          repeatState={{}}
          onRepeatCount={() => {}}
          idPrefix={`${idPrefix}-review`}
          highlightFieldKey={highlightFieldKey}
          formTitle={formTitle}
        />
      )}
    </div>
  );
}
